/**
 * components/ui/ConfirmModal.tsx
 * Reusable confirmation modal for destructive actions.
 * Usage: <ConfirmModal open={open} onConfirm={fn} onCancel={fn} title="..." message="..." />
 */

import React from 'react';
import { AlertTriangle, X } from 'lucide-react';

interface Props {
  open:      boolean;
  title:     string;
  message:   string;
  confirmLabel?: string;
  onConfirm: () => void;
  onCancel:  () => void;
  danger?:   boolean;
}

const ConfirmModal: React.FC<Props> = ({
  open, title, message, confirmLabel = 'Delete', onConfirm, onCancel, danger = true
}) => {
  if (!open) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={onCancel}
        style={{
          position: 'fixed', inset: 0,
          background: 'rgba(0,0,0,0.45)',
          backdropFilter: 'blur(3px)',
          zIndex: 1000,
          animation: 'fadeIn .15s ease',
        }}
      />

      {/* Modal */}
      <div style={{
        position:   'fixed',
        top:        '50%',
        left:       '50%',
        transform:  'translate(-50%, -50%)',
        zIndex:     1001,
        width:      'min(92vw, 420px)',
        background: '#FFFFFF',
        borderRadius: 20,
        boxShadow:  '0 24px 64px rgba(0,0,0,0.18)',
        padding:    '28px 28px 24px',
        animation:  'popIn .2s ease',
      }}>
        {/* Close */}
        <button
          onClick={onCancel}
          style={{ position:'absolute', top:16, right:16, background:'none', border:'none', cursor:'pointer', color:'#7A7570', padding:4, display:'flex', borderRadius:6 }}
        >
          <X size={16}/>
        </button>

        {/* Icon */}
        <div style={{
          width: 52, height: 52, borderRadius: 16,
          background: danger ? '#FEF2F2' : '#FFF4E0',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          marginBottom: 18,
        }}>
          <AlertTriangle size={24} color={danger ? '#DC2626' : '#C4811A'}/>
        </div>

        {/* Text */}
        <h2 style={{ fontFamily:"'Sora',sans-serif", fontWeight:800, fontSize:20, color:'#1A1814', marginBottom:10, letterSpacing:'-0.03em' }}>
          {title}
        </h2>
        <p style={{ fontSize:14, color:'#7A7570', lineHeight:1.7, fontFamily:"'DM Sans',sans-serif", marginBottom:24 }}>
          {message}
        </p>

        {/* Buttons */}
        <div style={{ display:'flex', gap:10 }}>
          <button
            onClick={onCancel}
            style={{ flex:1, height:44, borderRadius:10, border:'1.5px solid #E8E4DC', background:'#FAFAF8', cursor:'pointer', fontFamily:"'DM Sans',sans-serif", fontWeight:600, fontSize:14, color:'#3D3A34' }}
          >
            Cancel
          </button>
          <button
            onClick={() => { onConfirm(); }}
            style={{ flex:1, height:44, borderRadius:10, border:'none', background: danger ? 'linear-gradient(135deg,#EF4444,#DC2626)' : 'linear-gradient(135deg,#F5A623,#C4811A)', cursor:'pointer', fontFamily:"'DM Sans',sans-serif", fontWeight:700, fontSize:14, color:'#fff', boxShadow: danger ? '0 4px 14px #DC262633' : '0 4px 14px #F5A62333' }}
          >
            {confirmLabel}
          </button>
        </div>
      </div>

      <style>{`
        @keyframes fadeIn { from { opacity:0 } to { opacity:1 } }
        @keyframes popIn  { from { opacity:0; transform:translate(-50%,-48%) scale(0.95) } to { opacity:1; transform:translate(-50%,-50%) scale(1) } }
      `}</style>
    </>
  );
};

export default ConfirmModal;